import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Clock,
  Car,
  Star,
  Plane,
  Briefcase,
  Gem,
  Timer,
  Quote,
  MessageCircle,
  Wine,
} from "lucide-react";
import {
  heroImage,
  services,
  safeRide,
  testimonials,
  stats,
  chauffeurImage,
  contact,
} from "../mock";

const iconFor = {
  "airport-transfer": Plane,
  corporate: Briefcase,
  "special-events": Gem,
  hourly: Timer,
};

const Home = () => {
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Luxury black Escalade" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0c1620]/85 via-[#0c1620]/70 to-[#0c1620]" />
          <div className="absolute inset-0 bg-[#0c1620]/40" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-5 text-center pt-24 pb-16">
          <p className="reveal font-display text-xs sm:text-sm uppercase tracking-[0.5em] text-[var(--gold)] mb-6">
            Miami • Executive Chauffeur
          </p>
          <h1 className="reveal font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[1.05] text-white">
            Elevate Your Journey with{" "}
            <span className="text-gold-gradient">Shadow Path</span>
          </h1>
          <h2 className="reveal mt-6 font-display text-xl sm:text-3xl font-semibold text-gray-100">
            Premium Chauffeur &amp; SafeRide Services in Miami
          </h2>
          <p className="reveal mt-5 text-base sm:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Executive black-truck transportation, airport transfers, and SafeRide services — driven by professionals, designed for comfort and peace of mind.
          </p>

          <div className="reveal mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/book" className="btn-gold inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-widest">
              Book Now <ArrowRight size={17} />
            </Link>
            <a href={contact.whatsapp} target="_blank" rel="noreferrer" className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-widest">
              <MessageCircle size={17} /> Chat on WhatsApp
            </a>
          </div>

          <div className="reveal mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs sm:text-sm text-gray-300">
            {["Licensed", "Insured", "Escalade Fleet", "24/7 Service"].map((b) => (
              <span key={b} className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]" /> {b}
              </span>
            ))}
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2 text-gray-400">
          <span className="text-[10px] uppercase tracking-widest">Scroll</span>
          <span className="h-10 w-px bg-gradient-to-b from-[var(--gold)] to-transparent animate-pulse" />
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#0a121a] border-y border-white/5 py-10">
        <div className="max-w-6xl mx-auto px-5 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="reveal text-center">
              <div className="font-display text-3xl sm:text-4xl font-extrabold text-gold-gradient">{s.value}</div>
              <div className="mt-1 text-xs sm:text-sm uppercase tracking-widest text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="reveal text-center max-w-2xl mx-auto mb-14">
            <p className="font-display text-xs uppercase tracking-[0.4em] text-[var(--gold)] mb-3">What We Offer</p>
            <h2 className="font-display text-3xl sm:text-5xl font-extrabold text-white">
              Our Black Truck Service Experience
            </h2>
          </div>

          <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((s) => {
              const Icon = iconFor[s.id] || Car;
              return (
                <div key={s.id} className="reveal lux-card group rounded-2xl border border-white/10 bg-[#13212e] overflow-hidden">
                  <div className="img-zoom relative h-48">
                    <img src={s.image} alt={s.title} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#13212e] via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 flex h-10 w-10 items-center justify-center rounded-full bg-[var(--gold)] text-[#0c1620]">
                      <Icon size={19} />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg font-bold text-white">{s.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.short}</p>
                    <div className="mt-4 flex items-center justify-between">
                      {s.price ? (
                        <span className="text-sm font-semibold text-[var(--gold)]">
                          {s.price}{s.note ? ` · ${s.note}` : ""}
                        </span>
                      ) : (
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">Custom quote</span>
                      )}
                      <Link to="/services" className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-gray-300 group-hover:text-[var(--gold)] transition-colors">
                        Read More <ArrowRight size={13} />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 bg-[#0a121a] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal img-zoom relative rounded-2xl overflow-hidden border border-white/10">
            <img src={chauffeurImage} alt="Professional chauffeur" className="w-full h-[420px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0c1620]/60 to-transparent" />
          </div>
          <div>
            <p className="reveal font-display text-xs uppercase tracking-[0.4em] text-[var(--gold)] mb-3">The Shadow Path Standard</p>
            <h2 className="reveal font-display text-3xl sm:text-4xl font-extrabold text-white leading-tight">
              Discreet. Punctual. Effortlessly Luxurious.
            </h2>
            <p className="reveal mt-5 text-muted-foreground leading-relaxed">
              Every detail is engineered for your comfort — from immaculate blacked-out Escalades to chauffeurs trained in white-glove service. This is transportation as it should be.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              {[
                { icon: ShieldCheck, t: "Licensed & Insured", d: "Fully vetted, background-checked chauffeurs." },
                { icon: Clock, t: "24/7 Availability", d: "On call day or night, whenever you need us." },
                { icon: Car, t: "Escalade Fleet", d: "Immaculate blacked-out luxury SUVs." },
                { icon: Wine, t: "Luxury Add-Ons", d: "Champagne, roses, red carpet & more." },
              ].map((f) => (
                <div key={f.t} className="reveal flex gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--gold)]/10 border border-[var(--gold)]/30 text-[var(--gold)]">
                    <f.icon size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white text-sm">{f.t}</h4>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SAFERIDE CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={safeRide.heroImage} alt="Miami nightlife" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#0c1620]/85" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-5 text-center">
          <p className="reveal font-display text-xs uppercase tracking-[0.4em] text-[var(--gold)] mb-3">Get Home Safely in Luxury</p>
          <h2 className="reveal font-display text-3xl sm:text-5xl font-extrabold text-white">Shadow Path SafeRide</h2>
          <p className="reveal mt-5 text-lg text-gray-200 max-w-2xl mx-auto leading-relaxed">
            Enjoy your night worry-free. Our chauffeurs pick you up in a luxury Escalade while a second driver returns your car safely.
          </p>
          <div className="reveal mt-8 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {safeRide.benefits.slice(0, 3).map((b, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-5 text-sm text-gray-200 backdrop-blur-sm">
                {b}
              </div>
            ))}
          </div>
          <Link to="/saferide" className="reveal btn-gold mt-9 inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-widest">
            Book SafeRide Now <ArrowRight size={17} />
          </Link>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="reveal text-center max-w-2xl mx-auto mb-14">
            <p className="font-display text-xs uppercase tracking-[0.4em] text-[var(--gold)] mb-3">Guest Experiences</p>
            <h2 className="font-display text-3xl sm:text-5xl font-extrabold text-white">Loved by Miami's Finest</h2>
          </div>
          <div className="grid gap-7 md:grid-cols-3">
            {testimonials.map((t) => (
              <div key={t.name} className="reveal lux-card rounded-2xl border border-white/10 bg-[#13212e] p-8">
                <Quote className="text-[var(--gold)]/40" size={34} />
                <div className="mt-3 flex gap-1">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} size={16} className="fill-[var(--gold)] text-[var(--gold)]" />
                  ))}
                </div>
                <p className="mt-4 text-sm text-gray-200 leading-relaxed italic">“{t.text}”</p>
                <div className="mt-6 border-t border-white/10 pt-4">
                  <p className="font-semibold text-white text-sm">{t.name}</p>
                  <p className="text-xs text-[var(--gold)]">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="reveal relative overflow-hidden rounded-3xl border border-[var(--gold)]/25 bg-gradient-to-br from-[#13212e] to-[#0a121a] p-10 sm:p-16 text-center">
            <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-[var(--gold)]/10 blur-3xl" />
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white">Ready to Ride in Style?</h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">Reserve your blacked-out Escalade and experience Miami the Shadow Path way.</p>
            <Link to="/book" className="btn-gold mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-widest">
              Reserve Your Ride <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
